import { toast } from 'sonner';
import { getAttributionCartAttributes } from '@/lib/attribution';
import {
  SHOPIFY_STORE_DOMAIN as shopifyStoreDomain,
  SHOPIFY_STOREFRONT_TOKEN as shopifyStorefrontToken,
  getShopifyEndpoint,
  isShopifyConfigured,
  getShopifyDiagnostics,
  recordShopifyError,
  setHealthResult,
  SHOPIFY_API_VERSION,
} from '@/config/shopify';

// ─── Re-exports for consumers ───────────────────────────────────────────────
export { getShopifyEndpoint, isShopifyConfigured } from '@/config/shopify';

// ─── Shopify Domain for discount/coupon links ───────────────────────────────
export const SHOPIFY_DOMAIN = shopifyStoreDomain;

// ─── Boot-time diagnostics ──────────────────────────────────────────────────
const diag = getShopifyDiagnostics();
console.info(
  `[Shopify] Boot diagnostics:\n` +
  `  Domain  : ${diag.domain}\n` +
  `  Token   : ${diag.tokenMasked}\n` +
  `  Endpoint: ${diag.endpoint}\n` +
  `  Source  : domain=${diag.domainSource}, token=${diag.tokenSource}`
);
// DEV-only: confirm resolved endpoint on page load
if (import.meta.env.DEV) console.log('[Shopify] endpoint:', getShopifyEndpoint());

if (!diag.configured) {
  const missing: string[] = [];
  if (!diag.domain) missing.push('VITE_SHOPIFY_STORE_DOMAIN');
  if (diag.tokenStatus === 'MISSING') missing.push('VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN');
  console.error(
    '[Shopify] CRITICAL: Shopify is not fully configured.\n' +
    `  Missing env vars: ${missing.join(', ')}\n` +
    `  Domain: ${diag.domain || '(empty)'} | Token: ${diag.tokenStatus}\n` +
    '  These must be set as Cloud Secrets or in .env for the preview to work.'
  );
}

// Helper to build discount URLs with proper Shopify domain
export function buildDiscountUrl(
  couponCode: string, 
  options?: { 
    redirect?: string; 
    refCode?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
  }
): string {
  const baseUrl = `https://${SHOPIFY_DOMAIN}/discount/${couponCode}`;
  const params = new URLSearchParams();
  
  if (options?.redirect) {
    if (options.refCode) {
      if (options.redirect === '/cart') {
        params.set('redirect', `/cart?attributes[ref]=${options.refCode}`);
      } else {
        const separator = options.redirect.includes('?') ? '&' : '?';
        params.set('redirect', `${options.redirect}${separator}ref=${options.refCode}`);
      }
    } else {
      params.set('redirect', options.redirect);
    }
  }
  
  if (options?.utmSource) params.set('utm_source', options.utmSource);
  if (options?.utmMedium) params.set('utm_medium', options.utmMedium);
  if (options?.utmCampaign) params.set('utm_campaign', options.utmCampaign);
  
  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

// ─── Types ──────────────────────────────────────────────────────────────────
export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    productType: string;
    tags: string[];
    vendor: string;
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          sku: string | null;
          price: {
            amount: string;
            currencyCode: string;
          };
          compareAtPrice?: {
            amount: string;
            currencyCode: string;
          } | null;
          availableForSale: boolean;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
  };
}

// ─── GraphQL Queries ────────────────────────────────────────────────────────
const STOREFRONT_QUERY = `
  query GetProducts($first: Int!, $query: String) {
    products(first: $first, query: $query) {
      edges {
        node {
          id
          title
          description
          handle
          productType
          tags
          vendor
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
      variants(first: 10) {
            edges {
              node {
                id
                title
                sku
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          options {
            name
            values
          }
        }
      }
    }
  }
`;

const PRODUCT_BY_HANDLE_QUERY = `
  query GetProductByHandle($handle: String!) {
    product(handle: $handle) {
      id
      title
      description
      descriptionHtml
      handle
      productType
      tags
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 10) {
        edges {
          node {
            url
            altText
          }
        }
        nodes {
          url
          altText
        }
      }
      variants(first: 20) {
        edges {
          node {
            id
            title
            sku
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
      }
      options {
        name
        values
      }
      shippingWeightKg: metafield(namespace: "shipping", key: "weight_kg") {
        value
      }
      shippingLengthCm: metafield(namespace: "shipping", key: "length_cm") {
        value
      }
      shippingWidthCm: metafield(namespace: "shipping", key: "width_cm") {
        value
      }
      shippingHeightCm: metafield(namespace: "shipping", key: "height_cm") {
        value
      }
    }
  }
`;

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                  }
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// ─── Shop health-check query (lightweight) ──────────────────────────────────
const SHOP_INFO_QUERY = `{ shop { name } }`;

// ─── Toast dedup: only show one toast per error type ────────────────────────
let _toastShown = false;

// ─── API Helper ─────────────────────────────────────────────────────────────
export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  if (!isShopifyConfigured()) {
    const missingVars: string[] = [];
    if (!shopifyStoreDomain) missingVars.push('VITE_SHOPIFY_STORE_DOMAIN');
    if (!shopifyStorefrontToken) missingVars.push('VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN');
    const msg = `Variáveis ausentes: ${missingVars.join(', ')}`;
    console.error(`[Shopify] API call blocked — ${msg}`);
    recordShopifyError(msg);
    if (!_toastShown) {
      _toastShown = true;
      toast.error("Shopify: Configuração incompleta", { description: msg });
    }
    return null;
  }

  const isMutation = /^\s*mutation\b/.test(query);
  const maxAttempts = isMutation ? 1 : 3;
  const retryDelays = [300, 600];
  let response: Response | undefined;
  let lastError: unknown;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      response = await fetch(getShopifyEndpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': shopifyStorefrontToken
        },
        body: JSON.stringify({ query, variables }),
      });
      if (response.status < 500) break;
      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, retryDelays[attempt]));
      }
    } catch (err) {
      lastError = err;
      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, retryDelays[attempt]));
      }
    }
  }

  if (!response) {
    throw lastError;
  }

  if (response.status === 402) {
    const msg = 'HTTP 402 – Pagamento necessário na Shopify.';
    recordShopifyError(msg, 402);
    toast.error("Shopify: Pagamento necessário", {
      description: "O acesso à API do Shopify requer um plano de pagamento ativo.",
    });
    return null;
  }

  if (!response.ok) {
    const msg = `HTTP ${response.status}`;
    recordShopifyError(msg, response.status);
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  
  if (data.errors) {
    const msg = data.errors.map((e: { message: string }) => e.message).join(', ');
    recordShopifyError(msg);
    throw new Error(`Error calling Shopify: ${msg}`);
  }

  return data;
}

// ─── Boot-time connectivity test ────────────────────────────────────────────
if (isShopifyConfigured()) {
  storefrontApiRequest(SHOP_INFO_QUERY)
    .then((data) => {
      if (data?.data?.shop?.name) {
        setHealthResult('ok', 'Connection successful', data.data.shop.name);
        console.info(`[Shopify] ✅ Connection OK — shop: ${data.data.shop.name}`);
      } else {
        setHealthResult('failed', 'No shop data returned');
        console.warn('[Shopify] ⚠️ Connection returned no shop data', data);
      }
    })
    .catch((err) => {
      setHealthResult('failed', String(err));
      console.error('[Shopify] ❌ Connection test FAILED:', err);
    });
} else {
  setHealthResult('unconfigured', 'Shopify env vars are missing');
}

// ─── Collection by handle query ─────────────────────────────────────────────
const COLLECTION_BY_HANDLE_QUERY = `
  query GetCollectionByHandle($handle: String!, $first: Int!) {
    collection(handle: $handle) {
      id
      title
      handle
      products(first: $first) {
        edges {
          node {
            id
            title
            description
            handle
            productType
            tags
            vendor
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 5) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  sku
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                }
              }
            }
            options {
              name
              values
            }
          }
        }
      }
    }
  }
`;

// ─── Fetch Products ─────────────────────────────────────────────────────────
export async function fetchProducts(first: number = 20, query?: string): Promise<ShopifyProduct[]> {
  try {
    const data = await storefrontApiRequest(STOREFRONT_QUERY, { first, query });
    if (!data) return [];
    return data.data.products.edges;
  } catch (error) {
    if (import.meta.env.DEV) console.error('Error fetching products:', error);
    return [];
  }
}

// ─── Fetch Products by Collection Handle ────────────────────────────────────
export async function fetchProductsByCollection(collectionHandle: string, first: number = 50): Promise<ShopifyProduct[]> {
  try {
    const data = await storefrontApiRequest(COLLECTION_BY_HANDLE_QUERY, { handle: collectionHandle, first });
    if (!data) return [];
    const collection = data.data?.collection;
    if (!collection) {
      console.warn(`[Shopify] Collection not found: ${collectionHandle}`);
      return [];
    }
    return collection.products.edges;
  } catch (error) {
    if (import.meta.env.DEV) console.error(`Error fetching collection "${collectionHandle}":`, error);
    return [];
  }
}

// ─── Fetch Single Product by Handle ─────────────────────────────────────────
export async function fetchProductByHandle(handle: string) {
  try {
    if (import.meta.env.DEV) {
      console.log('[Shopify] fetchProductByHandle:', { handle, configured: isShopifyConfigured() });
    }
    const data = await storefrontApiRequest(PRODUCT_BY_HANDLE_QUERY, { handle });
    if (!data) {
      console.warn('[Shopify] productByHandle null for handle:', handle);
      return null;
    }
    if (!data.data.product) {
      console.warn('[Shopify] Product not found for handle:', handle);
      return null;
    }
    return data.data.product;
  } catch (error) {
    if (import.meta.env.DEV) console.error('Error fetching product:', error);
    return null;
  }
}

// ─── Cart / Checkout ────────────────────────────────────────────────────────
export interface CartItemForCheckout {
  variantId: string;
  quantity: number;
}

export async function createStorefrontCheckout(items: CartItemForCheckout[]): Promise<string | null> {
  try {
    const lines = items.map(item => ({
      quantity: item.quantity,
      merchandiseId: item.variantId,
    }));

    const returnUrlBase = `https://nathof.com.br/pedido-confirmado`;

    // Attribution (utm_*/gclid/fbclid from first touch) → cart attributes →
    // reaches the Shopify order as note_attributes (fixes customerJourney NULL).
    const attributionAttrs = getAttributionCartAttributes();

    const cartData = await storefrontApiRequest(CART_CREATE_MUTATION, {
      input: {
        lines,
        attributes: [{ key: "_return_url", value: returnUrlBase }, ...attributionAttrs],
      },
    });

    if (!cartData) return null;

    if (cartData.data.cartCreate.userErrors.length > 0) {
      throw new Error(`Cart creation failed: ${cartData.data.cartCreate.userErrors.map((e: { message: string }) => e.message).join(', ')}`);
    }

    const cart = cartData.data.cartCreate.cart;
    
    if (!cart.checkoutUrl) {
      throw new Error('No checkout URL returned from Shopify');
    }

    const url = new URL(cart.checkoutUrl);
    url.searchParams.set('channel', 'online_store');

    // Append return URL for purchase_completed tracking
    const returnUrl = `${returnUrlBase}?total=${encodeURIComponent(String(cart.cost?.totalAmount?.amount || '0'))}`;
    url.searchParams.set('return_url', returnUrl);

    return url.toString();
  } catch (error) {
    if (import.meta.env.DEV) console.error('Error creating storefront checkout:', error);
    throw error;
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────
export function formatPrice(amount: string | number, currencyCode: string = 'BRL'): string {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currencyCode,
  }).format(numAmount);
}

// ─── Shopify CDN on-the-fly image transform ─────────────────────────────────
// Returns the same cdn.shopify.com image URL with width + webp transform params.
// Idempotent: any pre-existing width/format params are replaced. Preserves other
// params (e.g. ?v=123). Non-Shopify URLs are returned unchanged.
export function shopifyImg(url: string | null | undefined, width: number): string {
  if (!url || !url.includes('cdn.shopify.com')) return url || '';
  try {
    const u = new URL(url);
    u.searchParams.delete('width');
    u.searchParams.delete('format');
    u.searchParams.set('width', String(Math.round(width)));
    u.searchParams.set('format', 'webp');
    return u.toString();
  } catch {
    return url;
  }
}

// Builds a 1x/2x srcset string for retina sharpness. Empty for non-Shopify URLs.
export function shopifyImgSrcSet(url: string | null | undefined, width: number): string {
  if (!url || !url.includes('cdn.shopify.com')) return '';
  return `${shopifyImg(url, width)} 1x, ${shopifyImg(url, width * 2)} 2x`;
}
